import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Marks a visit **arrived** or **no-show** on Zocdoc (`PUT /v1/appointments/update-status`).
 *
 * **One route for two statuses, unlike confirm and cancel** — because Zocdoc makes it one
 * endpoint taking an `appointment_status`. The rule this file follows is "mirror Zocdoc's own
 * split", not "one route per status": confirm, cancel and reschedule are separate endpoints with
 * disjoint payloads and so are separate routes, while these two share an endpoint, a payload and
 * a set of preconditions.
 *
 * `arrived` and `no_show` are the only values Zocdoc's endpoint accepts. There is **no way to put
 * an appointment back to pending** — no endpoint exists for it — so no such action is offered.
 *
 * **Writes nothing to FHIR**, like confirm.
 */
export const zocdocAppointmentStatus = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/status' })
  .input(
    z.object({
      zocdocAppointmentId: z.string().min(1),
      status: z.enum(['arrived', 'no_show']),
    }),
  )
  .output(
    z.object({
      status: z.enum([
        /** Zocdoc accepted the status write. */
        'updated',
        /** Zocdoc already reported this status — nothing was sent. */
        'unchanged',
        /** Nothing was sent. See `refusal`. */
        'refused',
      ]),
      refusal: z
        .object({
          reason: z.enum([
            'not-found',
            /** Cancelled, or already arrived/no-show: there is no visit to mark. */
            'status-not-markable',
            /** `no_show` needs a start in the past — the visit has not happened yet. */
            'not-past',
            /** `no_show` is refused for a start more than two days old, which is Zocdoc's rule. */
            'too-old',
            /** Zocdoc gave no readable start, so the `no_show` window cannot be checked. */
            'no-start',
            'zocdoc-rejected',
          ]),
          detail: z.string(),
        })
        .optional(),
      previousStatus: z.string().optional(),
      /**
       * What Zocdoc reported when the appointment was re-read **immediately after** the write.
       *
       * Exists because a successful write plus an unchanged row is otherwise silent: the screen
       * re-reads the list, shows the same status, and cannot say whether Zocdoc rejected the
       * change or has simply not caught up. Absent when the verifying read failed — which is not
       * itself a failure of the write.
       */
      observedStatus: z.string().optional(),
    }),
  )
